import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all duration-50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none aria-invalid:ring-destructive/20 aria-invalid:border-destructive cursor-pointer shadow-lg rounded-full",
  {
    variants: {
      variant: {
        default:
          "bg-action-solid text-on-accent hover:bg-action-solid-hover focus:ring-2 focus:ring-focus-ring-brand focus:ring-offset-1 active:ring-0",
        primary:
          "bg-primary primary-gradient text-on-primary hover:brightness-105 shadow-lg shadow-glow-primary/15 focus:ring-2 focus:ring-focus-ring-brand focus:ring-offset-1 active:ring-0",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-none focus-visible:ring-destructive/20",
        success:
          "bg-success-solid text-on-accent hover:bg-success-solid-hover shadow-lg shadow-glow-success/15 focus:ring-2 focus:ring-focus-ring-success focus:ring-offset-1 active:ring-0",
        outline:
          "border border-control-border bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-input-bg control-gradient text-secondary-foreground hover:bg-input-bg border border-control-border shadow-lg",
        ghost:
          "hover:bg-accent hover:text-accent-foreground bg-transparent shadow-none",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        default: "h-12 px-6 py-3 has-[>svg]:px-5",
        xs: "h-10 px-4 py-2 has-[>svg]:px-5",
        sm: "h-12 gap-1.5 px-4 has-[>svg]:px-5",
        lg: "h-12 px-8 has-[>svg]:px-6",
        icon: "size-12"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };

